import React, { useEffect, useMemo, useState } from 'react';
import { locName, devicesAtSite, deviceSubLocation, subLocationsFor } from '../../utils/locations';
import Pager, { usePaged } from './Pager';

// Move several devices between the sub-locations of one site.
//
// The per-device sidebar dropdown handles a correction; this handles a hand-over, where a
// whole compound's worth of kit moves from one part of a site to another. Without it that
// is one sidebar visit per device.
//
// Scoped to a single site on purpose: `location` is unchanged throughout, so this can
// never be a way to move devices between sites by accident. Deploy and dispatch own that.

const COLS = '28px 120px 90px minmax(0,1fr)';

function TransferSubLocation({ loc, st, onCancel, onConfirm }) {
  const site = locName(loc);

  // Manual sub-locations only. A portal project is assigned by the portal, so offering it
  // as a destination would promise something the next sync would undo.
  const subs = useMemo(() => subLocationsFor(loc, st).filter(s => s.origin === 'manual'), [loc, st]);

  const [from, setFrom] = useState('');   // '' = every device on site
  const [to, setTo] = useState('');
  const [picked, setPicked] = useState(() => new Set());
  const [page, setPage] = useState(1);

  // Devices on this site, each with where it currently sits.
  const rows = useMemo(() => {
    return devicesAtSite(st.devices || [], loc)
      .map(d => {
        const cur = deviceSubLocation(d, st);
        return {
          device: d,
          serial: d.serial || '—',
          type: d.type || '—',
          current: cur,
          // A portal-assigned device cannot be moved by hand: the portal decides where it
          // sits, and a manual tag would be advisory at best and misleading at worst.
          locked: !!cur && cur.origin === 'portal',
        };
      })
      .sort((a, b) => String(a.serial).localeCompare(String(b.serial)));
  }, [loc, st]);

  const shown = useMemo(() => rows.filter(r => {
    if (from === '__none') return !r.current;
    if (from) return r.current && r.current.name === from;
    return true;
  }), [rows, from]);

  // Every row that COULD move, across all pages — the selection and the footer count are
  // about the whole filtered set, not just what is on screen.
  const movable = shown.filter(r => !r.locked && (!r.current || r.current.name !== to));
  const selected = movable.filter(r => picked.has(r.device.id));
  const lockedCount = shown.filter(r => r.locked).length;

  // A site can hold hundreds of devices, so the list pages rather than growing the modal
  // without bound. Paging sits after filtering, matching the Locations lists.
  const { paged, page: safePage, pageCount, from: rangeFrom, to: rangeTo } = usePaged(shown, page, setPage);

  // Changing either end of the move re-filters the list, so page 4 of the old list would
  // be an empty page of the new one.
  useEffect(() => { setPage(1); }, [from, to]);

  const toggle = id => setPicked(p => {
    const n = new Set(p);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  // Select-all covers THIS PAGE only. With paging, a box that silently selected rows you
  // cannot see would make the count in the footer a surprise; selecting everything is a
  // separate, explicitly-worded action below.
  const pageMovable = paged.filter(r => !r.locked && (!r.current || r.current.name !== to));
  const allPicked = pageMovable.length > 0 && pageMovable.every(r => picked.has(r.device.id));
  const toggleAll = () => setPicked(p => {
    const n = new Set(p);
    if (allPicked) pageMovable.forEach(r => n.delete(r.device.id));
    else pageMovable.forEach(r => n.add(r.device.id));
    return n;
  });
  const selectEverything = () => setPicked(new Set(movable.map(r => r.device.id)));
  const clearSelection = () => setPicked(new Set());

  const canSubmit = selected.length > 0 && (to !== '' || from !== '');
  const control = {
    height: 32, padding: '0 10px', borderRadius: 6, border: '1px solid var(--border-default)',
    background: 'var(--surface-default)', color: 'var(--text-primary)',
    fontFamily: 'inherit', fontSize: 13.5, outline: 'none',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Move devices between sub-locations of ${site}`}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="card" style={{ width: 720, maxWidth: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--border-default)', flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>Move between sub-locations</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              Within {site} — devices stay on this location.
            </div>
          </div>
          <button type="button" onClick={onCancel} aria-label="Close" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: 0 }}>×</button>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--border-default)', flexWrap: 'wrap', flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 170 }}>
            <label htmlFor="tsl-from" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Showing</label>
            <select id="tsl-from" value={from} onChange={e => { setFrom(e.target.value); setPicked(new Set()); }} style={{ ...control, width: '100%' }}>
              <option value="">Everything on {site}</option>
              <option value="__none">No sub-location yet</option>
              {subs.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </div>
          <div aria-hidden="true" style={{ paddingBottom: 8, color: 'var(--text-subtle)' }}>→</div>
          <div style={{ flex: 1, minWidth: 170 }}>
            <label htmlFor="tsl-to" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Move to</label>
            <select id="tsl-to" value={to} onChange={e => setTo(e.target.value)} style={{ ...control, width: '100%' }}>
              <option value="">No sub-location</option>
              {subs.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: '0 12px', height: 34, alignItems: 'center', padding: '0 18px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-default)', fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-muted)', position: 'sticky', top: 0 }}>
            <input
              type="checkbox" checked={allPicked} onChange={toggleAll}
              disabled={!pageMovable.length}
              aria-label={allPicked ? 'Clear selection on this page' : 'Select every device on this page'} />
            <div>Serial</div><div>Type</div><div>Sub-location</div>
          </div>

          {shown.length === 0 && (
            <div style={{ padding: '32px 18px', textAlign: 'center' }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>Nothing to show</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                {from ? 'No device matches that filter.' : `No devices are on ${site}.`}
              </div>
            </div>
          )}

          {paged.map(r => {
            const already = !!r.current && r.current.name === to;
            const disabled = r.locked || already;
            return (
              <label
                key={r.device.id}
                style={{ display: 'grid', gridTemplateColumns: COLS, gap: '0 12px', alignItems: 'center', minHeight: 42, padding: '0 18px', borderBottom: '1px solid var(--border-subtle)', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1 }}>
                <input
                  type="checkbox"
                  checked={picked.has(r.device.id) && !disabled}
                  disabled={disabled}
                  onChange={() => toggle(r.device.id)}
                  aria-label={`Select ${r.serial}`} />
                <span className="tnum" style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-primary)' }}>{r.serial}</span>
                <span style={{ fontSize: 13.5, color: 'var(--text-muted)' }}>{r.type}</span>
                <span style={{ fontSize: 13, color: 'var(--text-muted)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.current ? r.current.name : <em>None</em>}
                  {r.locked && <span style={{ color: 'var(--text-subtle)' }}> · set by the portal</span>}
                  {already && <span style={{ color: 'var(--text-subtle)' }}> · already here</span>}
                </span>
              </label>
            );
          })}
        </div>

        {/* Range, the select-everything escape hatch, and the pager. Selecting across
            pages has to be asked for explicitly — see the toggleAll note above. */}
        {shown.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 18px', borderTop: '1px solid var(--border-default)', background: 'var(--surface-inset)', flexShrink: 0, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {rangeFrom}–{rangeTo} of {shown.length}
              {shown.length === rows.length ? '' : ` (filtered from ${rows.length})`}
            </span>
            {movable.length > paged.length && selected.length < movable.length && (
              <button type="button" onClick={selectEverything}
                style={{ border: 'none', background: 'transparent', padding: 0, fontFamily: 'inherit', fontSize: 12, color: 'var(--brand-light)', cursor: 'pointer', textDecoration: 'underline' }}>
                Select all {movable.length} matching
              </button>
            )}
            {selected.length > 0 && (
              <button type="button" onClick={clearSelection}
                style={{ border: 'none', background: 'transparent', padding: 0, fontFamily: 'inherit', fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', textDecoration: 'underline' }}>
                Clear selection
              </button>
            )}
            <div style={{ marginLeft: 'auto' }}>
              <Pager page={safePage} pageCount={pageCount} setPage={setPage} label="Devices" />
            </div>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderTop: '1px solid var(--border-default)', flexShrink: 0, flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 180, fontSize: 12.5, color: 'var(--text-muted)' }}>
            {selected.length
              ? `${selected.length} device${selected.length === 1 ? '' : 's'} → ${to || 'no sub-location'}`
              : 'Select the devices to move.'}
            {selected.length > 0 && selected.some(r => !paged.includes(r)) && (
              <span style={{ color: 'var(--text-subtle)' }}> (some on other pages)</span>
            )}
            {lockedCount > 0 && (
              <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-subtle)', marginTop: 2 }}>
                {lockedCount} assigned by the OWL portal and cannot be moved by hand.
              </span>
            )}
          </span>
          <button type="button" onClick={onCancel} className="btn btn-ghost">Cancel</button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => onConfirm(selected.map(r => r.device.id), to)}
            className="btn btn-primary"
            style={{ opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
            Move {selected.length || ''} device{selected.length === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TransferSubLocation;
